'use client';

import { useEffect, useRef } from 'react';

interface UseFocusTrapOptions {
  isActive: boolean;
  autoFocus?: boolean;
  restoreFocus?: boolean;
}

export function useFocusTrap({ isActive, autoFocus = true, restoreFocus = true }: UseFocusTrapOptions) {
  const elementRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const element = elementRef.current;
    if (!element) return;

    // Store the previously focused element
    if (restoreFocus) {
      previousActiveElement.current = document.activeElement;
    }

    // Get all focusable elements
    const getFocusableElements = () => {
      const focusableSelectors = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]'
      ].join(', ');

      return Array.from(element.querySelectorAll(focusableSelectors)) as HTMLElement[];
    };

    const focusableElements = getFocusableElements();
    const firstElement = focusableElements[0];

    // Auto-focus the first element
    if (autoFocus && firstElement) {
      firstElement.focus();
    }

    // Handle Tab key navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const currentFocusableElements = getFocusableElements();
      const currentFirstElement = currentFocusableElements[0];
      const currentLastElement = currentFocusableElements[currentFocusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab: moving backwards
        if (document.activeElement === currentFirstElement) {
          e.preventDefault();
          currentLastElement?.focus();
        }
      } else {
        // Tab: moving forwards
        if (document.activeElement === currentLastElement) {
          e.preventDefault();
          currentFirstElement?.focus();
        }
      }
    };

    // Handle Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Trigger custom escape event for parent to handle
        element.dispatchEvent(new CustomEvent('focus-trap-escape', { bubbles: true }));
      }
    };

    element.addEventListener('keydown', handleKeyDown);
    element.addEventListener('keydown', handleEscape);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
      element.removeEventListener('keydown', handleEscape);

      // Restore focus to previously active element
      if (restoreFocus && previousActiveElement.current) {
        (previousActiveElement.current as HTMLElement).focus?.();
      }
    };
  }, [isActive, autoFocus, restoreFocus]);

  return elementRef;
}